/**
 * Where a tap on a notification lands: THE one table, for every channel.
 *
 *  - Native (Android / iOS): push.service.ts handleDeepLink, through
 *    notification-router.ts.
 *  - Browser: public/sw.js `importScripts('/notification-routes.js')`, which
 *    is THIS file compiled to a classic script (scripts/lib/notification-routes-sw.mjs;
 *    the dev server serves it, the build emits it and pins sw.js to its hash).
 *
 * So this file imports nothing (a worker has no module loader; the build
 * refuses a file that does) and touches no DOM or app state.
 *
 * The server sends `data.type` plus ids (entity_id, booking_id, session_id),
 * and `route`: where the tap lands, built by NotificationRoutes.php from the
 * same rules as this table (decision 2, docs/plans/NOTIFICATION-TAP-ROUTES.md).
 * resolveNotificationRoute honours it when it is a plain in-app path; this
 * table is the fallback, and the contract fixture holds the server's answer
 * for every type so the two cannot drift. The list of types it sends is
 * NotificationCatalog::sentTypes(); __tests__/notification-types-contract.test.ts
 * fails when one of them has no target here.
 *
 * Ids must be plain positive numbers, so a payload can never steer a tap into
 * another path. A type with a bad or missing id lands on the page it would
 * have opened from: /app/empathy (circles tab) for circle and group types,
 * the 1:1 tab for 1:1 types.
 */

export type RoutePayload = { type?: unknown; [key: string]: unknown };

/** What a tap on an incoming-call notification asked for (RING-PUSH). */
export type IncomingCallAction = 'join' | 'not_now';

export function isIncomingCallAction(value: unknown): value is IncomingCallAction {
  return value === 'join' || value === 'not_now';
}

/**
 * The Course page. Its own path, `/app`, cannot pass the approved route
 * pattern (it needs a segment after /app/), so a push about the course names
 * `/app/paths`, the router's long-standing alias that redirects to `/app`:
 * the server's route and this table say the same thing, and the tap lands
 * on the Course page either way.
 */
const COURSE = '/app/paths';
/**
 * The circles tab. `/app/empathy` is a combined hub (1on1 + circles) that
 * DEFAULTS to circles.
 */
const CIRCLES = '/app/empathy';
/**
 * The 1:1 tab. 1:1 types must say so: a bare `/app/empathy` dropped a
 * matched or pinged user on circles instead of the give/receive screen (the
 * bug Laura's session surfaced). empathy.tsx reads `?tab=1on1`; the preflight
 * opens from there, so this stops at the tab and does not auto-join.
 */
const ONE_ON_ONE = '/app/empathy?tab=1on1';
const PROFILE = '/app/profile';
const WALLET = '/app/wallet';

/** Where every incoming-call landing starts (and the page a call opens from). */
export const INCOMING_CALL_PAGE = ONE_ON_ONE;

/** The id when it is a plain positive number, else null. */
function numericId(value: unknown): string | null {
  const id = String(value ?? '');
  return /^[1-9]\d*$/.test(id) ? id : null;
}

/**
 * The incoming-call landing: the 1:1 page, plus the session to ring for and
 * the action tapped, when they are well formed. ONE builder for the native
 * tap, the web push click and any in-app link.
 */
export function incomingCallRoute(sessionId: unknown, action?: unknown): string {
  const id = numericId(sessionId);
  if (id === null) return INCOMING_CALL_PAGE;
  const act = isIncomingCallAction(action) ? `&call_action=${action}` : '';
  return `${INCOMING_CALL_PAGE}&call=${id}${act}`;
}

/** /app/bookings/{id} for a numeric id, else the 1:1 tab (never a broken path). */
function bookingRoute(bookingId: unknown, join = false): string {
  const id = numericId(bookingId);
  if (id === null) return ONE_ON_ONE;
  return `/app/bookings/${id}${join ? '?join=1' : ''}`;
}

/** A group's page (plus a suffix), or the circles tab without a group id. */
function cohortRoute(entityId: unknown, suffix = ''): string {
  const id = numericId(entityId);
  return id === null ? CIRCLES : `/app/circles/cohorts/${id}${suffix}`;
}

/** A circle's room, or the circles tab without a circle id. */
function circleRoute(entityId: unknown): string {
  const id = numericId(entityId);
  return id === null ? CIRCLES : `/app/circles/${id}`;
}

type Resolver = (payload: RoutePayload) => string;

const routes = {
  admin_test: () => COURSE,

  // ── 1:1 empathy: the 1:1 tab ───────────────────────────────────────────
  empathy_queue: () => ONE_ON_ONE,
  match_found: () => ONE_ON_ONE,
  giver_pinged: () => ONE_ON_ONE,
  try_other_side: () => ONE_ON_ONE,
  // P7 (B10): ~10 minutes before your listening hours. The 1:1 tab holds the
  // "Your listening" card with Pause for today.
  listening_hours_reminder: () => ONE_ON_ONE,
  // P7 + RING-PUSH: a listener is calling about your request. ALWAYS the 1:1
  // page, whatever the request's origin (David 2026-10-19 16:02), with the
  // ring sheet over it: `call` names the session (the landing polls at once
  // and the sheet shows for it), `call_action` a Join / Not now tapped on
  // the notification itself (the sheet runs it).
  empathy_request_claimed: ({ session_id, call_action }) =>
    incomingCallRoute(session_id, call_action),
  // D34: a listener called about your request but you weren't there; the
  // request is still up. A group's request goes back to that group's Empathy
  // tab; a request with no group (entity_id '') to the 1:1 tab it was made on.
  empathy_request_missed: ({ entity_id }) => {
    const id = numericId(entity_id);
    return id === null ? ONE_ON_ONE : `/app/circles/cohorts/${id}?tab=empathy`;
  },
  // P8: booked 1:1 calls (BookingNotifier) carry booking_id. "Starting now"
  // joins from the booked-call screen, through the gated join (D32).
  empathy_booking_confirmed: ({ booking_id }) => bookingRoute(booking_id),
  empathy_booking_reminder: ({ booking_id }) => bookingRoute(booking_id),
  empathy_booking_cancelled: ({ booking_id }) => bookingRoute(booking_id),
  empathy_booking_moved: ({ booking_id }) => bookingRoute(booking_id),
  empathy_booking_starting: ({ booking_id }) => bookingRoute(booking_id, true),

  // ── Circles: a circle's room, or the circles tab ───────────────────────
  circle_starting: ({ entity_id }) => circleRoute(entity_id),
  cohort_session_starting: ({ entity_id }) => circleRoute(entity_id),
  // "A circle is forming": the forming card is on the circles tab.
  circle_availability: () => CIRCLES,
  // "You're the only one there": back to the circles tab to pick another.
  circle_solo_fallback: () => CIRCLES,

  // ── Groups (cohorts): that group's page ────────────────────────────────
  cohort_session_reminder: ({ entity_id }) => cohortRoute(entity_id),
  cohort_session_rescheduled: ({ entity_id }) => cohortRoute(entity_id),
  cohort_session_cancelled: ({ entity_id }) => cohortRoute(entity_id),
  cohort_schedule_changed: ({ entity_id }) => cohortRoute(entity_id),
  cohort_paused: ({ entity_id }) => cohortRoute(entity_id),
  cohort_resumed: ({ entity_id }) => cohortRoute(entity_id),
  cohort_missed_session: ({ entity_id }) => cohortRoute(entity_id),
  cohort_join_approved: ({ entity_id }) => cohortRoute(entity_id),
  cohort_join_request: ({ entity_id }) => cohortRoute(entity_id, '/dashboard'),
  // The 48-hour session note: the group page, sessions tab.
  cohort_session_note: ({ entity_id }) => cohortRoute(entity_id, '?tab=sessions'),
  // P7: a group member asks for empathy -> that group's Empathy tab, where
  // the card and its Call button are.
  empathy_request_group: ({ entity_id }) => cohortRoute(entity_id, '?tab=empathy'),
  // There is no group page to open any more.
  cohort_deleted: () => CIRCLES,
  cohort_join_declined: () => CIRCLES,

  // ── Billing and wallet ─────────────────────────────────────────────────
  payment_failed: () => PROFILE,
  payment_failed_grace_period: () => PROFILE,
  subscription_expiring: () => PROFILE,
  subscription_expiring_1day: () => PROFILE,
  subscription_on_hold: () => PROFILE,
  trial_expiring: () => PROFILE,
  trial_expiring_1day: () => PROFILE,
  tip_received: () => WALLET,
  tip_sent: () => WALLET,
  withdrawal_failed: () => WALLET,
  withdrawal_success: () => WALLET,

  // ── The course itself ──────────────────────────────────────────────────
  welcome_nudge: () => COURSE,
  we_miss_you: () => COURSE,
  daily_digest: () => COURSE,
  daily_digest_streak: () => COURSE,
  daily_digest_feedback: () => '/app/conversations',
} satisfies Record<string, Resolver>;

export type NotificationType = keyof typeof routes;

/** Every type this table names (the contract tests walk it). */
export const NOTIFICATION_TYPES = Object.keys(routes) as NotificationType[];

/**
 * A `route` the server may send: an in-app path under /app/, lowercase,
 * with an optional plain query. Never protocol-relative, never `..` or `.`
 * segments, never a fragment, never outside /app.
 */
const SAFE_ROUTE = /^\/app\/[a-z0-9/_-]+(\?[a-z0-9=&_]*)?$/;

export function isSafeAppRoute(route: unknown): route is string {
  return typeof route === 'string' && SAFE_ROUTE.test(route) && !route.includes('//');
}

/**
 * A type this build does not know (a newer server than the bundle) lands by
 * its prefix, so an old build degrades to the right area and never to the
 * Course page (decision 3, 2026-10-31): a group's page, the circles tab, the
 * 1:1 tab, else the empathy hub.
 */
function fallbackRoute(type: string, payload: RoutePayload): string {
  if (type.indexOf('cohort_') === 0) return cohortRoute(payload.entity_id);
  if (type.indexOf('empathy_') === 0) return ONE_ON_ONE;
  return CIRCLES;
}

/**
 * A button tapped on the notification itself (an incoming call's Join / Not
 * now) belongs to the tap, not the push, so the server's route cannot carry
 * it: it is added here to a route that rings for a session (`call=`).
 */
function withTapAction(route: string, payload: RoutePayload): string {
  if (!isIncomingCallAction(payload.call_action)) return route;
  if (!/[?&]call=[1-9]\d*(&|$)/.test(route) || /[?&]call_action=/.test(route)) return route;
  return `${route}&call_action=${payload.call_action}`;
}

/**
 * Where a tap lands. The server's own `route` first (decision 2: every push
 * carries one, built by App\Services\Notifications\NotificationRoutes from
 * the same rules as this table, so a new type or a moved destination ships
 * with a server deploy), honoured only when it is a plain in-app path
 * (isSafeAppRoute); else this build's table; else the prefix fallback.
 */
export function resolveNotificationRoute(payload: RoutePayload): string {
  if (isSafeAppRoute(payload.route)) return withTapAction(payload.route, payload);
  const type = typeof payload.type === 'string' ? payload.type : '';
  const resolver: Resolver | undefined = Object.prototype.hasOwnProperty.call(routes, type)
    ? (routes as Record<string, Resolver>)[type]
    : undefined;
  return resolver ? resolver(payload) : fallbackRoute(type, payload);
}
